/**
 * services.ts
 *
 * Full API layer for user services — mirrors mobile UserServiceService.dart
 *
 * Endpoints:
 *  GET    /profiles/services/             → own services list (paginated)
 *  POST   /profiles/services/             → create service (JSON)
 *  GET    /profiles/services/{id}/        → service detail
 *  PATCH  /profiles/services/{id}/        → update service (form-data, indexed keys)
 *  DELETE /profiles/services/{id}/        → delete service
 *  GET    /custom_account/user-types/     → available user types
 *  GET    /custom_account/tags/           → available tags
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api';
import { toast } from '../utils/toast';
import { extractApiError } from '../utils/apiError';
import { formatTagLabel } from '../utils/tagLabel';
import { normalizeServicePriceValue } from '../utils/price';

// ─── Types ────────────────────────────────────────────────────────────────────

/** A user-type object from the API */
export interface UserTypeItem {
  id: number;
  name: string;
}

/** A tag object from the API */
export interface TagItem {
  id: number;
  name: string;
  category?: string;
}

/** Creator object embedded in API service responses */
export interface ServiceCreator {
  id: number;
  full_name: string;
  email: string;
  image?: string;
  profile_image?: string;
  username?: string;
}

/** Normalised service object used throughout the UI */
export interface ServiceItem {
  id: number;
  title: string;
  description?: string;
  price?: string;
  /** Cloudinary image URLs */
  images: string[];
  work_example_links: string[];
  user: number;
  /** Creator object returned by newer API endpoints */
  creator?: ServiceCreator;
  user_types: UserTypeItem[];
  /** API field is `user_tags`; some endpoints alias to `tags` — both handled */
  user_tags: TagItem[];
  created_at: string;
  /** Optional contact email for this service */
  alternate_email?: string;
}

export interface ServiceListResponse {
  results: ServiceItem[];
  count: number;
  next: string | null;
  previous: string | null;
}

export interface CreateServicePayload {
  title: string;
  description?: string;
  price?: string;
  /** Already-uploaded Cloudinary URLs */
  images?: string[];
  work_example_links?: string[];
  user_types?: number[];
  tags?: number[];
  alternate_email?: string;
}

export interface UpdateServicePayload extends Omit<CreateServicePayload, 'images' | 'tags'> {
  tags?: number[];
  alternate_email?: string;
  serviceId: number;
  /** New images already uploaded to Cloudinary */
  newImageUrls?: string[];
  /** Existing URLs retained from edit mode */
  existingImageUrls?: string[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Robustly parse images from whatever the API returns:
 *  - string[]              → used as-is
 *  - comma-separated str   → split
 *  - Python-list str       → stripped and split
 */
function parseImages(raw: unknown): string[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(String).filter(Boolean);
  if (typeof raw === 'string') {
    const s = raw.trim();
    if (!s) return [];
    if (s.startsWith('[')) {
      return s
        .replace(/[['\]'"]/g, '')
        .split(',')
        .map((u) => u.trim())
        .filter(Boolean);
    }
    if (s.includes(','))
      return s
        .split(',')
        .map((u) => u.trim())
        .filter(Boolean);
    return [s];
  }
  return [];
}

function normaliseTagItem(rawTag: unknown): TagItem | null {
  if (!rawTag) return null;

  if (typeof rawTag === 'string') {
    const name = formatTagLabel(rawTag);
    if (!name) return null;
    return { id: 0, name };
  }

  if (typeof rawTag === 'object') {
    const record = rawTag as Record<string, unknown>;
    const nameCandidate = (
      record.name ??
      record.tag_name ??
      record.label ??
      record.title ??
      ''
    ).toString();
    const name = formatTagLabel(nameCandidate);
    if (!name) return null;

    const idCandidate = Number(record.id ?? record.tag_id ?? 0);
    const id = Number.isFinite(idCandidate) ? idCandidate : 0;
    const category = typeof record.category === 'string' ? record.category : undefined;

    return { id, name, category };
  }

  return null;
}

function parseServiceTags(raw: Record<string, unknown>): TagItem[] {
  const userTags = Array.isArray(raw.user_tags) ? raw.user_tags : [];
  const tags = Array.isArray(raw.tags) ? raw.tags : [];
  const source = userTags.length > 0 ? userTags : tags;
  return source.map(normaliseTagItem).filter((tag): tag is TagItem => Boolean(tag));
}

/** Normalise a raw API object into a typed `ServiceItem`. */
function normaliseService(raw: Record<string, unknown>): ServiceItem {
  const parsedTags = parseServiceTags(raw);

  const rawCreator = raw.creator as Record<string, unknown> | undefined;

  return {
    id: raw.id as number,
    title: (raw.title as string) ?? '',
    description: raw.description as string | undefined,
    price: normalizeServicePriceValue(raw.price as string | undefined) || undefined,
    images: parseImages(raw.images),
    work_example_links: parseImages(raw.work_example_links),
    user: (raw.user as number) ?? 0,
    creator: rawCreator
      ? {
          id: rawCreator.id as number,
          full_name: (rawCreator.full_name as string) ?? '',
          email: (rawCreator.email as string) ?? '',
          image: (rawCreator.image ||
            rawCreator.profile_image ||
            rawCreator.prof_pics ||
            rawCreator.prof_pics_url) as string | undefined,
          username: rawCreator.username as string | undefined,
        }
      : undefined,
    user_types: (raw.user_types as UserTypeItem[]) ?? [],
    user_tags: parsedTags,
    alternate_email: raw.alternate_email as string | undefined,
    created_at: (raw.created_at as string) ?? '',
  };
}

// ─── Queries ─────────────────────────────────────────────────────────────────

/** Fetch services for a specific user by userId — used on public profile views.
 *  The public profile endpoint returns services in the profile payload, but we
 *  also support calling the authenticated services list filtered by context.
 *  For now we hit GET /profiles/services/ which returns the caller's own services;
 *  for other users we rely on the profile payload from useGetPublicProfile.
 */
export const useGetUserServices = (userId: string | number | undefined) => {
  return useQuery<ServiceItem[]>({
    queryKey: ['services', 'user', userId],
    queryFn: async () => {
      console.log(
        `[API] Fetching services for user ${userId} via /profiles/services/?user_id=${userId}`,
      );
      const { data } = await apiClient.get(`/profiles/services/?user_id=${userId}&page_size=50`);
      const items: Record<string, unknown>[] = Array.isArray(data)
        ? data
        : ((data?.results ?? data?.data ?? []) as Record<string, unknown>[]);
      console.log(`[API] Services response for ${userId}:`, items);
      return items.map(normaliseService);
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
};

/** Fetch the logged-in user's own services. Mirrors mobile fetchServices(). */
export const useGetMyServices = (page = 1, options?: { enabled?: boolean }) => {
  return useQuery<ServiceItem[]>({
    queryKey: ['services', 'my', page],
    enabled: options?.enabled !== false,
    queryFn: async () => {
      console.log('[API] Fetching logged-in user services via /profiles/services/');
      const { data } = await apiClient.get(`/profiles/services/?page=${page}&page_size=20`);
      // Handle: plain array, { results: [] }, { data: [] }
      const items: Record<string, unknown>[] = Array.isArray(data)
        ? data
        : ((data?.results ?? data?.data ?? []) as Record<string, unknown>[]);
      console.log('[API] Logged-in user services response:', items);
      return items.map(normaliseService);
    },
  });
};

/** Fetch a single service by ID. Mirrors mobile fetchServiceDetail(). */
export const useGetServiceDetail = (serviceId: string | number) => {
  return useQuery<ServiceItem>({
    queryKey: ['services', serviceId],
    queryFn: async () => {
      const { data } = await apiClient.get<Record<string, unknown>>(
        `/profiles/services/${serviceId}/`,
      );
      // Handle optional envelope wrapper
      const raw = (data as { data?: Record<string, unknown> }).data ?? data;
      return normaliseService(raw as Record<string, unknown>);
    },
    enabled: !!serviceId,
  });
};

/** Fetch a single public service — no auth required. GET /public/services/{id}/ */
export const useGetPublicServiceDetail = (serviceId: string | number) => {
  return useQuery<ServiceItem>({
    queryKey: ['services', 'public', serviceId],
    queryFn: async () => {
      const { data } = await apiClient.get<Record<string, unknown>>(
        `/public/services/${serviceId}/`,
      );
      // Response: { success: true, service: { ... } }
      const raw = (data?.service ?? data?.data ?? data?.results ?? data) as Record<string, unknown>;
      return normaliseService(raw);
    },
    enabled: !!serviceId,
  });
};

/** Fetch all public services — no auth required. GET /public/services/ */
export const useGetPublicServices = (page = 1, options?: { enabled?: boolean }) => {
  return useQuery<ServiceListResponse>({
    queryKey: ['services', 'public-list', page],
    enabled: options?.enabled !== false,
    queryFn: async () => {
      const { data } = await apiClient.get(`/public/services/?page=${page}&page_size=20`);
      // API returns: { count, next, previous, results: { success, results: [...] } }
      // — double-nested. Handle all variants gracefully.
      const inner = data?.results;
      const items: Record<string, unknown>[] = Array.isArray(data)
        ? data
        : Array.isArray(inner)
          ? inner
          : Array.isArray((inner as Record<string, unknown>)?.results)
            ? (inner as Record<string, unknown>).results
            : Array.isArray(data?.data)
              ? data.data
              : [];
      return {
        results: items.map(normaliseService),
        count: data?.count ?? items.length,
        next: data?.next ?? null,
        previous: data?.previous ?? null,
      };
    },
  });
};

/**
 * Fetch available user types.
 * Mirrors mobile UserTypeSelector — filters out 'fan' and 'admin', sorts A-Z.
 */
export const useGetUserTypes = () => {
  return useQuery<UserTypeItem[]>({
    queryKey: ['user-types'],
    queryFn: async () => {
      const { data } = await apiClient.get<unknown>('/custom_account/user-types/');
      // Backend wraps response: { success, message, data: [...] }
      // Handle plain array, { data: [] }, and { results: [] } envelopes.
      const all: UserTypeItem[] = Array.isArray(data)
        ? (data as UserTypeItem[])
        : (((data as Record<string, unknown>)?.data ??
            (data as Record<string, unknown>)?.results ??
            []) as UserTypeItem[]);
      return all
        .filter((t) => {
          const lower = t.name.toLowerCase();
          return lower !== 'fan' && lower !== 'admin';
        })
        .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
    },
    staleTime: 10 * 60 * 1000,
  });
};

/**
 * Fetch available tags.
 * Mirrors mobile ServiceTagSelector — client-side search filter.
 */
export const useGetTags = (search = '') => {
  return useQuery<TagItem[]>({
    queryKey: ['tags', search],
    queryFn: async () => {
      const { data } = await apiClient.get<unknown>('/custom_account/tags/');
      // Backend wraps response: { success, message, data: [...] }
      // Handle plain array, { data: [] }, and { results: [] } envelopes.
      const all: TagItem[] = Array.isArray(data)
        ? (data as TagItem[])
        : (((data as Record<string, unknown>)?.data ??
            (data as Record<string, unknown>)?.results ??
            []) as TagItem[]);
      const formatted = all.map((tag) => ({ ...tag, name: formatTagLabel(tag.name) }));
      const sorted = [...formatted].sort((a, b) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
      );
      if (!search.trim()) return sorted;
      const lower = search.toLowerCase();
      return sorted.filter((t) => t.name.toLowerCase().includes(lower));
    },
    staleTime: 10 * 60 * 1000,
  });
};

// ─── Mutations ────────────────────────────────────────────────────────────────

/**
 * Create a new service.
 * Mirrors mobile createService() — POST with JSON body, images as array of URLs.
 * Images must already be uploaded to Cloudinary before calling this.
 */
export const useCreateService = () => {
  const queryClient = useQueryClient();
  return useMutation<ServiceItem, Error, CreateServicePayload>({
    mutationFn: async (payload) => {
      const body: Record<string, unknown> = { title: payload.title };
      if (payload.description) body.description = payload.description;
      const normalizedPrice = normalizeServicePriceValue(payload.price);
      if (normalizedPrice) body.price = normalizedPrice;
      if (payload.images?.length) body.images = payload.images;
      if (payload.work_example_links?.length) body.work_example_links = payload.work_example_links;
      // Always include tags / user_types — send empty array when none
      // selected so the backend initialises the relation to empty (mirrors mobile)
      body.tags = payload.tags ?? [];
      body.user_types = payload.user_types ?? [];
      if (payload.alternate_email) body.alternate_email = payload.alternate_email;

      const { data } = await apiClient.post<Record<string, unknown>>('/profiles/services/', body);
      return normaliseService(data);
    },
    onSuccess: () => {
      toast.success('Service created successfully');
      queryClient.invalidateQueries({ queryKey: ['services'] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to create service'));
    },
  });
};

/**
 * Update an existing service.
 * Mirrors mobile updateService() — PATCH with form-data using indexed array keys
 * (e.g. images[0], images[1], user_tags[0], ...) exactly like mobile patchFormData.
 */
export const useUpdateService = () => {
  const queryClient = useQueryClient();
  return useMutation<ServiceItem, Error, UpdateServicePayload>({
    mutationFn: async ({ serviceId, newImageUrls = [], existingImageUrls = [], ...payload }) => {
      // Combine: new uploads first, then retained existing — mirrors mobile logic
      const allImages = [...newImageUrls, ...existingImageUrls];

      // Use a JSON PATCH body (same as CREATE) — no actual File objects are sent
      // here since images are pre-uploaded to Cloudinary and only URLs are passed.
      // JSON handles empty arrays for user_tags / user_types reliably, avoiding
      // the multipart/form-data "Expected a list but got bool" DRF parse error
      // that occurs when an array field is entirely absent from a FormData body.
      const body: Record<string, unknown> = {
        title: payload.title,
        tags: payload.tags ?? [],
        user_types: payload.user_types ?? [],
        images: allImages,
        work_example_links: payload.work_example_links ?? [],
      };
      if (payload.description !== undefined && payload.description.trim()) {
        body.description = payload.description.trim();
      }
      if (payload.price !== undefined) body.price = normalizeServicePriceValue(payload.price);
      if (payload.alternate_email !== undefined) body.alternate_email = payload.alternate_email;

      const { data } = await apiClient.patch<Record<string, unknown>>(
        `/profiles/services/${serviceId}/`,
        body,
      );
      return normaliseService(data);
    },
    onSuccess: (_data, vars) => {
      toast.success('Service updated');
      queryClient.invalidateQueries({ queryKey: ['services'] });
      queryClient.invalidateQueries({ queryKey: ['services', vars.serviceId] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Update failed'));
    },
  });
};

/**
 * Delete a service by ID.
 * Mirrors mobile deleteService().
 */
export const useDeleteService = () => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: async (serviceId) => {
      await apiClient.delete(`/profiles/services/${serviceId}/`);
    },
    onSuccess: () => {
      toast.success('Service deleted');
      queryClient.invalidateQueries({ queryKey: ['services'] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Deletion failed'));
    },
  });
};

/**
 * Apply to a service (unauthenticated).
 * POST /profiles/services/{id}/applications/
 */
export const useApplyToService = () => {
  return useMutation({
    mutationFn: async ({
      serviceId,
      data,
    }: {
      serviceId: number | string;
      data: {
        email: string;
        sender_role?: 'inquirer' | 'organizer';
        message_html: string;
        screen_width: number;
        screen_height: number;
        recaptcha_token?: string;
      };
      suppressToast?: boolean;
    }) => {
      const { data: responseData } = await apiClient.post(
        `/profiles/services/${serviceId}/applications/`,
        data,
      );
      return responseData;
    },
    onSuccess: (_data, variables) => {
      if (!variables?.suppressToast) {
        toast.success('Application submitted successfully!');
      }
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to submit application'));
    },
  });
};

// ─── Service Applications (Inquiries for user services) ──────────────────────

export type ServiceApplicationStatus = 'new' | 'in_conversation' | 'contracted' | 'closed';

export interface ServiceApplicationsFilterParams {
  status?: ServiceApplicationStatus;
  date_preset?: 'today' | 'this_week' | 'this_month' | 'custom';
  start_date?: string;
  end_date?: string;
  service_id?: number | string;
  timezone?: string;
  page?: number;
}

export interface ServiceApplicationListItem {
  application_uuid: string;
  status: ServiceApplicationStatus;
  created_at: string;
  applicant_email: string;
  service: {
    id: number;
    title: string;
  };
}

export interface ServiceApplicationDetail {
  application_uuid: string;
  status: ServiceApplicationStatus;
  applicant_email: string;
  message: string;
  created_at: string;
  updated_at: string;
  service: {
    id: number;
    title: string;
    description: string;
  };
}

export interface ServiceApplicationsListResponse {
  success: boolean;
  message: string;
  counts: {
    all: number;
    new: number;
    in_conversation: number;
    contracted: number;
    closed: number;
  };
  data: {
    count: number;
    next: string | null;
    previous: string | null;
    results: ServiceApplicationListItem[];
  };
}

export interface ServiceApplicationDetailResponse {
  success: boolean;
  message: string;
  data: ServiceApplicationDetail;
}

/** List service applications for services owned by authenticated user */
export const useGetServiceApplications = (params: ServiceApplicationsFilterParams = {}) => {
  return useQuery({
    queryKey: ['service-applications', params],
    staleTime: 0,
    gcTime: 0,
    queryFn: async () => {
      try {
        const sp = new URLSearchParams();
        Object.entries(params).forEach(([k, v]) => {
          if (v !== undefined && v !== '' && v !== null) sp.set(k, String(v));
        });
        const qs = sp.toString();
        const { data } = await apiClient.get<ServiceApplicationsListResponse>(
          `/profiles/services/applications/${qs ? `?${qs}` : ''}`,
        );
        return data;
      } catch (error) {
        console.error('Failed to fetch service applications:', error);
        return {
          success: false,
          message: 'Failed to load inquiries',
          counts: { all: 0, new: 0, in_conversation: 0, contracted: 0, closed: 0 },
          data: { count: 0, next: null, previous: null, results: [] },
        } satisfies ServiceApplicationsListResponse;
      }
    },
  });
};

/** Get detail of a single service application */
export const useGetServiceApplication = (applicationUuid: string | undefined) => {
  return useQuery({
    queryKey: ['service-application', applicationUuid],
    staleTime: 30_000,
    queryFn: async () => {
      const { data } = await apiClient.get<ServiceApplicationDetailResponse>(
        `/profiles/services/applications/${applicationUuid}/`,
      );
      return data.data;
    },
    enabled: !!applicationUuid,
  });
};

/** Update status of a service application */
export const useUpdateServiceApplication = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      applicationUuid,
      status,
    }: {
      applicationUuid: string;
      status: ServiceApplicationStatus;
      suppressToast?: boolean;
    }) => {
      const { data } = await apiClient.patch<ServiceApplicationDetailResponse>(
        `/profiles/services/applications/${applicationUuid}/`,
        { status },
      );
      return data;
    },
    onSuccess: (_data, { applicationUuid, suppressToast }) => {
      if (!suppressToast) {
        toast.success('Inquiry updated successfully');
      }
      queryClient.invalidateQueries({ queryKey: ['service-applications'] });
      queryClient.invalidateQueries({ queryKey: ['service-application', applicationUuid] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to update inquiry'));
    },
  });
};
